import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/LangContext";
import { pickLocalized, labelType } from "@/lib/i18n";
import Navbar from "@/components/Navbar";
import HeritageCard from "@/components/HeritageCard";
import HeritageMap from "@/components/HeritageMap";
import HeritageForm from "@/components/HeritageForm";
import HeritageDetail from "@/components/HeritageDetail";
import DeleteDialog from "@/components/DeleteDialog";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  SquaresFour,
  ListBullets,
  MapTrifold,
  Plus,
  Buildings,
  Calendar,
  MapPin,
  DownloadSimple,
  Shield,
  UserCircle,
} from "@phosphor-icons/react";
import { formatApiErrorDetail } from "@/lib/api";

const TYPES = ["All", "Building", "Monument", "Site"];

export default function PublicBrowse() {
  const { user } = useAuth();
  const { lang, t } = useI18n();
  const canCreate = !!user;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [mineOnly, setMineOnly] = useState(false);
  const [view, setView] = useState("grid");

  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (typeFilter && typeFilter !== "All") params.type = typeFilter;
      const { data } = await api.get("/heritage", { params });
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("toast.load.failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t2 = setTimeout(load, 250);
    return () => clearTimeout(t2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, typeFilter]);

  const visibleItems = useMemo(() => {
    if (!mineOnly || !user) return items;
    return items.filter((i) => i.created_by === user.id);
  }, [items, mineOnly, user]);

  const stats = useMemo(() => {
    const byAuthority = {};
    items.forEach((i) => {
      const key = i.local_authority || "Unspecified";
      byAuthority[key] = (byAuthority[key] || 0) + 1;
    });
    return { total: items.length, byAuthority };
  }, [items]);

  const openAdd = () => { setEditItem(null); setFormOpen(true); };
  const openEdit = (it) => { setEditItem(it); setFormOpen(true); };
  const confirmDelete = async () => {
    if (!deleteItem) return;
    try {
      await api.delete(`/heritage/${deleteItem.id}`);
      toast.success(t("toast.deleted"));
      setDeleteItem(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("toast.delete.failed"));
    }
  };

  const oldest = items.filter((i) => i.year_built).reduce(
    (min, i) => (i.year_built < min ? i.year_built : min),
    9999
  );

  return (
    <div className="min-h-screen bg-[#f9f8f6]">
      <Navbar />

      <section className="border-b border-[#e5e3db] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 lg:py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <div className="overline mb-4">{t("hero.overline")}</div>
            <h1 className="serif text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02] text-[#1a1a1a]">
              {t("hero.title.a")}<br />{t("hero.title.b")}
            </h1>
            <p className="mt-5 text-base text-[#5c5c5c] max-w-2xl leading-relaxed">
              {t("hero.subtitle")}
            </p>
          </div>
          <div className="lg:col-span-4 border-l border-[#e5e3db] pl-8">
            <div className="grid grid-cols-2 gap-4">
              <HeroStat icon={<Buildings size={18} weight="duotone" />} label={t("hero.stat.records")} value={stats.total} />
              <HeroStat icon={<Shield size={18} weight="duotone" />} label={t("hero.stat.under_dbku")} value={stats.byAuthority["DBKU"] || 0} />
              <HeroStat icon={<Shield size={18} weight="duotone" />} label={t("hero.stat.under_mbks")} value={stats.byAuthority["MBKS"] || 0} />
              <HeroStat icon={<Shield size={18} weight="duotone" />} label={t("hero.stat.under_bau")} value={stats.byAuthority["Bau District Council"] || 0} />
            </div>
          </div>
        </div>
      </section>

      <section
        className="sticky top-16 z-30 bg-[#f9f8f6]/95 backdrop-blur-sm border-b border-[#e5e3db]"
        data-testid="browse-controls"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex flex-col md:flex-row md:items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5c5c5c]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("controls.search")}
              className="w-full h-10 pl-9 pr-3 text-sm bg-white border border-[#e5e3db] focus:border-[#b25a38] focus:outline-none focus:ring-1 focus:ring-[#b25a38] transition-colors"
              data-testid="browse-search-input"
            />
          </div>

          <div className="flex items-center gap-1 overflow-x-auto -mx-1 px-1" data-testid="browse-type-filters">
            {TYPES.map((tp) => (
              <button
                key={tp}
                onClick={() => setTypeFilter(tp)}
                className={`whitespace-nowrap text-xs uppercase tracking-[0.16em] font-semibold px-3 h-8 border transition-colors duration-150 ${
                  typeFilter === tp
                    ? "bg-[#1a1a1a] text-white border-[#1a1a1a]"
                    : "bg-white text-[#5c5c5c] border-[#e5e3db] hover:text-[#1a1a1a]"
                }`}
                data-testid={`filter-type-${tp.toLowerCase()}`}
              >
                {tp === "All" ? t("controls.type.all") : labelType(tp, lang)}
              </button>
            ))}
            {user && (
              <button
                onClick={() => setMineOnly((v) => !v)}
                className={`whitespace-nowrap text-xs uppercase tracking-[0.16em] font-semibold px-3 h-8 border ml-1 transition-colors duration-150 inline-flex items-center gap-1.5 ${
                  mineOnly
                    ? "bg-[#b25a38] text-white border-[#b25a38]"
                    : "bg-white text-[#5c5c5c] border-[#e5e3db] hover:text-[#b25a38] hover:border-[#b25a38]"
                }`}
                data-testid="filter-mine"
                aria-pressed={mineOnly}
              >
                <UserCircle size={13} weight="duotone" />
                {t("controls.mine")}
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <div className="flex border border-[#e5e3db] bg-white" data-testid="view-toggle">
              <ViewBtn active={view === "grid"} onClick={() => setView("grid")} label={t("controls.view.grid")} testid="view-grid"><SquaresFour size={16} weight="duotone" /></ViewBtn>
              <ViewBtn active={view === "list"} onClick={() => setView("list")} label={t("controls.view.list")} testid="view-list"><ListBullets size={16} weight="duotone" /></ViewBtn>
              <ViewBtn active={view === "map"} onClick={() => setView("map")} label={t("controls.view.map")} testid="view-map"><MapTrifold size={16} weight="duotone" /></ViewBtn>
            </div>

            {canCreate && (
              <button
                onClick={openAdd}
                className="inline-flex items-center gap-2 h-10 px-4 bg-[#b25a38] text-white text-sm hover:bg-[#93492c] transition-colors duration-150"
                data-testid="add-heritage-btn"
              >
                <Plus size={16} weight="bold" />
                {t("controls.add")}
              </button>
            )}

            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/heritage/export/csv`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 h-10 px-4 border border-[#e5e3db] bg-white text-sm text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white hover:border-[#1a1a1a] transition-colors duration-150"
              data-testid="csv-export-btn"
            >
              <DownloadSimple size={16} weight="duotone" />
              <span className="hidden sm:inline">{t("controls.csv")}</span>
            </a>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 lg:py-14">
        {loading ? (
          <div className="text-sm text-[#5c5c5c]" data-testid="browse-loading">{t("empty.loading")}</div>
        ) : visibleItems.length === 0 ? (
          <div className="border border-dashed border-[#d9d5c8] p-16 text-center bg-white">
            <div className="serif text-2xl text-[#1a1a1a] mb-2">{t("empty.title")}</div>
            <p className="text-sm text-[#5c5c5c]">{t("empty.body")}</p>
          </div>
        ) : view === "map" ? (
          <HeritageMap items={visibleItems} onOpen={setDetailItem} />
        ) : view === "list" ? (
          <div className="border border-[#e5e3db] bg-white divide-y divide-[#e5e3db]" data-testid="list-view">
            {visibleItems.map((it) => (
              <ListRow key={it.id} item={it} user={user} onOpen={setDetailItem} onEdit={openEdit} onDelete={setDeleteItem} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="grid-view">
            {visibleItems.map((it) => (
              <HeritageCard key={it.id} item={it} user={user} onOpen={setDetailItem} onEdit={openEdit} onDelete={setDeleteItem} />
            ))}
          </div>
        )}
      </section>

      <HeritageForm open={formOpen} onOpenChange={setFormOpen} initial={editItem} onSaved={load} />
      <HeritageDetail
        item={detailItem}
        open={!!detailItem}
        onOpenChange={(o) => !o && setDetailItem(null)}
        user={user}
        onEdit={openEdit}
        onDelete={setDeleteItem}
      />
      <DeleteDialog open={!!deleteItem} onOpenChange={(o) => !o && setDeleteItem(null)} item={deleteItem} onConfirm={confirmDelete} />

      <footer className="border-t border-[#e5e3db] mt-10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-[#5c5c5c]">
          <div>
            <span className="serif text-sm text-[#1a1a1a] font-bold">{t("footer.brand")}</span> · {t("footer.tagline")}
          </div>
          <div className="uppercase tracking-[0.22em]">{t("footer.credit")}</div>
        </div>
      </footer>
    </div>
  );
}

function HeroStat({ icon, label, value }) {
  return (
    <div>
      <div className="text-[#b25a38] mb-2">{icon}</div>
      <div className="serif text-3xl font-black text-[#1a1a1a] leading-none">{value}</div>
      <div className="overline mt-2">{label}</div>
    </div>
  );
}

function ViewBtn({ active, onClick, label, children, testid }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 h-10 px-3 text-xs uppercase tracking-[0.16em] font-semibold border-r border-[#e5e3db] last:border-r-0 transition-colors duration-150 ${
        active ? "bg-[#1a1a1a] text-white" : "text-[#5c5c5c] hover:text-[#1a1a1a]"
      }`}
      data-testid={testid}
      aria-pressed={active}
    >
      {children}
      <span className="hidden md:inline">{label}</span>
    </button>
  );
}

function ListRow({ item, user, onOpen, onEdit, onDelete }) {
  const { lang, t } = useI18n();
  const canEdit = !!user;
  const name = pickLocalized(item, "name", lang);
  const description = pickLocalized(item, "description", lang);
  const cover = (item.image_urls && item.image_urls[0]) || item.image_url;
  return (
    <div className="flex flex-col md:flex-row items-stretch group hover:bg-[#faf8f4] transition-colors" data-testid={`list-row-${item.id}`}>
      <button
        type="button"
        onClick={() => onOpen(item)}
        className="w-full md:w-52 aspect-[16/10] md:aspect-auto md:h-32 bg-[#efece4] overflow-hidden flex-shrink-0 text-left"
      >
        {cover ? (
          <img
            src={cover.startsWith("http") ? cover : `${process.env.REACT_APP_BACKEND_URL}${cover}`}
            alt={name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center serif italic text-sm text-[#b7b0a0]">No image</div>
        )}
      </button>
      <div className="flex-1 p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#b25a38]">{labelType(item.type, lang)}</span>
            <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5c5c5c]">· {item.local_authority || "—"}</span>
          </div>
          <button type="button" onClick={() => onOpen(item)} className="serif text-xl font-bold text-[#1a1a1a] hover:text-[#b25a38] transition-colors text-left">
            {name}
          </button>
          <p className="text-sm text-[#5c5c5c] mt-1 line-clamp-2 leading-relaxed">{description}</p>
        </div>
        <div className="flex md:flex-col md:items-end gap-4 md:gap-1 md:min-w-[140px] md:text-right text-xs text-[#5c5c5c]">
          <div>
            <div className="overline">{t("admin.col.year")}</div>
            <div className="text-[#1a1a1a]">{item.year_built || "—"}</div>
          </div>
          <div>
            <div className="overline">{t("detail.condition")}</div>
            <div className="text-[#1a1a1a]">{item.condition}</div>
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 md:pl-4 md:border-l md:border-[#e5e3db]">
            <button onClick={() => onEdit(item)} className="text-xs uppercase tracking-[0.18em] font-semibold text-[#5c5c5c] hover:text-[#b25a38]" data-testid={`list-edit-${item.id}`}>Edit</button>
            <button onClick={() => onDelete(item)} className="text-xs uppercase tracking-[0.18em] font-semibold text-[#5c5c5c] hover:text-[#d32f2f]" data-testid={`list-delete-${item.id}`}>Delete</button>
          </div>
        )}
      </div>
    </div>
  );
}
